'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { jwtDecode } from 'jwt-decode';
import { useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/lib/api/auth';
import { isAllowedRole, ALLOWED_ROLES, type AllowedRole } from '@/lib/auth-utils';

// ── Types ──────────────────────────────────────────────────────────────────

export type { AllowedRole };
export { ALLOWED_ROLES };

export interface AuthUser {
  id: number;
  username: string;
  nombre: string;
  rol: AllowedRole;
  empresa_id: string;
  is_superuser: boolean;
}

interface JwtPayload {
  user_id: number;
  rol: AllowedRole;
  empresa_id: string;
  is_superuser: boolean;
  exp: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  updateNombre: (nombre: string) => void;
}

// ── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Helpers ────────────────────────────────────────────────────────────────

const TOKEN_KEY = 'token';
const NOMBRE_KEY = 'auth_nombre';
const USERNAME_KEY = 'auth_username';

function getStorage(): Storage {
  // localStorage persiste entre sesiones en móvil.
  // sessionStorage se limpia al cerrar pestaña — causa bucles de login en móvil.
  try { return localStorage; } catch { return sessionStorage; }
}

function buildUserFromStorage(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  const storage = getStorage();
  const token = storage.getItem(TOKEN_KEY);
  if (!token) return null;
  try {
    const payload = jwtDecode<JwtPayload>(token);
    // Check expiry
    if (payload.exp * 1000 < Date.now()) {
      storage.removeItem(TOKEN_KEY);
      storage.removeItem(NOMBRE_KEY);
      storage.removeItem(USERNAME_KEY);
      return null;
    }
    // Validate role
    if (!isAllowedRole(payload.rol)) {
      storage.removeItem(TOKEN_KEY);
      storage.removeItem(NOMBRE_KEY);
      storage.removeItem(USERNAME_KEY);
      return null;
    }
    const nombre = storage.getItem(NOMBRE_KEY) ?? '';
    const username = storage.getItem(USERNAME_KEY) ?? '';
    return {
      id: payload.user_id,
      username,
      nombre,
      rol: payload.rol,
      empresa_id: payload.empresa_id,
      is_superuser: payload.is_superuser ?? false,
    };
  } catch {
    return null;
  }
}

// ── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const qc = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate from sessionStorage on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const u = buildUserFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setUser(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setIsLoading(false);
  }, []);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const data = await authApi.login(identifier, password);
      // token is already stored by authApi.login
      getStorage().setItem(NOMBRE_KEY, data.nombre)
      getStorage().setItem(USERNAME_KEY, identifier.trim())
      // Setear cookie de sesión para el middleware server-side
      // No contiene el JWT — solo indica que hay una sesión activa.
      document.cookie = 'auth_session=1; path=/; SameSite=Strict; max-age=28800' // 8h = JWT expiry

      const payload = jwtDecode<JwtPayload>(data.token);
      setUser({
        id: payload.user_id,
        username: identifier.trim(),
        nombre: data.nombre,
        rol: payload.rol,
        empresa_id: payload.empresa_id,
        is_superuser: payload.is_superuser ?? false,
      });
    },
    []
  );

  const updateNombre = useCallback((nombre: string) => {
    getStorage().setItem(NOMBRE_KEY, nombre);
    setUser(prev => prev ? { ...prev, nombre } : prev);
  }, []);

  const logout = useCallback(() => {
    authApi.logout()
    // Limpiar cookie de sesión del middleware
    document.cookie = 'auth_session=; path=/; SameSite=Strict; max-age=0'
    getStorage().removeItem(NOMBRE_KEY)
    getStorage().removeItem(USERNAME_KEY)
    setUser(null);
    // Limpiar toda la caché de TanStack Query para evitar que datos del tenant
    // anterior sean visibles si otro usuario inicia sesión en la misma pestaña
    qc.clear();
    router.push('/login');
  }, [router, qc]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, updateNombre }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
